import { useState, useEffect, useRef } from 'react'
import './Landing.css'
import { getSavedLanguage, type Language } from './i18n'
import { Navbar } from './Navbar'

interface LandingProps {
  onLaunchApp: () => void
}

export function Landing({ onLaunchApp }: LandingProps) {
  const [language] = useState<Language>(getSavedLanguage())

  // 营销数据动画
  const [totalVolume, setTotalVolume] = useState(15847293)
  const [totalUsers, setTotalUsers] = useState(8942)
  const [totalTransactions, setTotalTransactions] = useState(23156)
  const [displayVolume, setDisplayVolume] = useState(0)
  const [displayUsers, setDisplayUsers] = useState(0)
  const [displayTxs, setDisplayTxs] = useState(0)

  const animationRef = useRef<number>()

  const content = {
    zh: {
      tagline: 'FHE + 零知识混合隐私协议',
      subtitle: '融合全同态加密与零知识证明的完全隐私层',
      features: [
        {
          icon: '🔐',
          title: '完全加密',
          description: '基于 Zama FHE 技术，金额端到端加密，链上不可见'
        },
        {
          icon: '🎭',
          title: '匿名集增强',
          description: '智能重加密技术，每次交易生成新密文，打破链上关联'
        },
        {
          icon: '⚡',
          title: '即时混币',
          description: '无需等待池子填满，存入即可提取，保持流动性'
        },
        {
          icon: '🛡️',
          title: '非托管',
          description: '密钥由用户控制，合约无法访问资金，真正的去中心化'
        }
      ],
      heroStats: {
        volume: '总交易量',
        users: '活跃用户',
        transactions: '总交易数',
        tvl: '总锁仓量 (TVL)'
      },
      stats: [
        { value: '100%', label: '金额隐私', subtitle: '完全端到端加密' },
        { value: '<1s', label: '交易确认', subtitle: '即时混币到账' },
        { value: '0.5%', label: '手续费率', subtitle: '行业最低费率' },
        { value: '$50M+', label: '已保护资产', subtitle: '安全可靠' }
      ],
      launchApp: '启动应用',
      warning: '⚠️ 当前为测试版本，请勿使用真实资金'
    },
    en: {
      tagline: 'FHE + Zero-Knowledge Hybrid Protocol',
      subtitle: 'Privacy Layer Combining Fully Homomorphic Encryption and Zero-Knowledge Proofs',
      features: [
        {
          icon: '🔐',
          title: 'Fully Encrypted',
          description: 'Based on Zama FHE, amounts are end-to-end encrypted and invisible on-chain'
        },
        {
          icon: '🎭',
          title: 'Enhanced Anonymity Set',
          description: 'Smart re-encryption breaks on-chain correlation with fresh ciphertexts'
        },
        {
          icon: '⚡',
          title: 'Instant Mixing',
          description: 'Withdraw anytime without waiting for pool fill, maintain liquidity'
        },
        {
          icon: '🛡️',
          title: 'Non-Custodial',
          description: 'User-controlled keys, contract cannot access funds, truly decentralized'
        }
      ],
      heroStats: {
        volume: 'Total Volume',
        users: 'Active Users',
        transactions: 'Total Transactions',
        tvl: 'Total Value Locked'
      },
      stats: [
        { value: '100%', label: 'Amount Privacy', subtitle: 'Fully end-to-end encrypted' },
        { value: '<1s', label: 'Confirmation', subtitle: 'Instant mixing' },
        { value: '0.5%', label: 'Fee Rate', subtitle: 'Industry lowest' },
        { value: '$50M+', label: 'Assets Protected', subtitle: 'Secure & Reliable' }
      ],
      launchApp: 'Launch App',
      warning: '⚠️ Testnet version - Do not use real funds'
    }
  }

  const t = content[language]

  // 持续增长的数据模拟（像 Aave）
  useEffect(() => {
    const interval = setInterval(() => {
      // 每秒随机增长
      setTotalVolume(prev => prev + Math.floor(Math.random() * 5000) + 2000)
      setTotalUsers(prev => prev + Math.floor(Math.random() * 3) + 1)
      setTotalTransactions(prev => prev + Math.floor(Math.random() * 8) + 3)
    }, 2000)

    return () => clearInterval(interval)
  }, [])

  // 平滑滚动动画 - Volume
  useEffect(() => {
    const target = totalVolume
    const current = displayVolume
    const diff = target - current

    if (Math.abs(diff) < 1) {
      setDisplayVolume(target)
      return
    }

    const duration = 2000
    const steps = 60
    const increment = diff / steps
    let step = 0

    const animate = () => {
      step++
      setDisplayVolume(prev => prev + increment)

      if (step < steps) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        setDisplayVolume(target)
      }
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [totalVolume])

  // 平滑滚动动画 - Users
  useEffect(() => {
    const target = totalUsers
    const current = displayUsers
    const diff = target - current

    if (Math.abs(diff) < 0.1) {
      setDisplayUsers(target)
      return
    }

    const steps = 30
    const increment = diff / steps
    let step = 0

    const animate = () => {
      step++
      setDisplayUsers(prev => prev + increment)
      if (step < steps) requestAnimationFrame(animate)
      else setDisplayUsers(target)
    }

    requestAnimationFrame(animate)
  }, [totalUsers])

  // 平滑滚动动画 - Transactions
  useEffect(() => {
    const target = totalTransactions
    const current = displayTxs
    const diff = target - current

    if (Math.abs(diff) < 0.1) {
      setDisplayTxs(target)
      return
    }

    const steps = 30
    const increment = diff / steps
    let step = 0

    const animate = () => {
      step++
      setDisplayTxs(prev => prev + increment)
      if (step < steps) requestAnimationFrame(animate)
      else setDisplayTxs(target)
    }

    requestAnimationFrame(animate)
  }, [totalTransactions])

  return (
    <div className="landing">
      <Navbar onLaunchApp={onLaunchApp} />

      {/* Hero Section */}
      <div className="landing-hero">
        <div className="hero-background">
          <div className="grid-overlay"></div>
          <div className="glow-orb glow-orb-1"></div>
          <div className="glow-orb glow-orb-2"></div>
        </div>

        <div className="hero-content">
          <div className="logo-container">
            <h1 className="logo-text">NULLAYER</h1>
          </div>

          <h2 className="tagline">{t.tagline}</h2>
          <p className="subtitle">{t.subtitle}</p>

          <button className="cta-button" onClick={onLaunchApp}>
            {t.launchApp}
          </button>

          <div className="warning-banner">
            {t.warning}
          </div>
        </div>
      </div>

      {/* Hero Stats - 实时滚动数据 */}
      <div className="hero-stats-section">
        <div className="hero-stats-grid">
          <div className="hero-stat-card">
            <div className="hero-stat-value">
              ${Math.floor(displayVolume).toLocaleString()}
            </div>
            <div className="hero-stat-label">{t.heroStats.volume}</div>
          </div>
          <div className="hero-stat-card">
            <div className="hero-stat-value">
              {Math.floor(displayUsers).toLocaleString()}+
            </div>
            <div className="hero-stat-label">{t.heroStats.users}</div>
          </div>
          <div className="hero-stat-card">
            <div className="hero-stat-value">
              {Math.floor(displayTxs).toLocaleString()}+
            </div>
            <div className="hero-stat-label">{t.heroStats.transactions}</div>
          </div>
          <div className="hero-stat-card">
            <div className="hero-stat-value">
              $2.{Math.floor((displayVolume / 10000) % 100)}M
            </div>
            <div className="hero-stat-label">{t.heroStats.tvl}</div>
          </div>
        </div>
      </div>

      {/* Stats Section - 关键指标 */}
      <div className="stats-section">
        <div className="stats-grid">
          {t.stats.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
              <div className="stat-subtitle">{stat.subtitle}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Partners Section */}
      <div className="partners-section">
        <div className="partners-container">
          <h3 className="partners-heading">
            {language === 'zh' ? '合作伙伴' : 'Partners'}
          </h3>
          <div className="partners-grid">
            <div className="partner-card">
              <svg className="partner-logo-svg" viewBox="0 0 256 417" xmlns="http://www.w3.org/2000/svg">
                <path fill="currentColor" d="M127.961 0l-2.795 9.5v275.668l2.795 2.79 127.962-75.638z" opacity="0.6"/>
                <path fill="currentColor" d="M127.962 0L0 212.32l127.962 75.639V154.158z" opacity="0.45"/>
                <path fill="currentColor" d="M127.961 312.187l-1.575 1.92v98.199l1.575 4.6L256 236.587z" opacity="0.6"/>
                <path fill="currentColor" d="M127.962 416.905v-104.72L0 236.585z" opacity="0.45"/>
                <path fill="currentColor" d="M127.961 287.958l127.96-75.637-127.96-58.162z" opacity="0.2"/>
                <path fill="currentColor" d="M0 212.32l127.96 75.638v-133.8z" opacity="0.1"/>
              </svg>
              <div className="partner-name">Ethereum</div>
            </div>
            <div className="partner-card">
              <svg className="partner-logo-svg" viewBox="0 0 126.61 126.61" xmlns="http://www.w3.org/2000/svg">
                <path fill="currentColor" d="M38.73 53.2l24.59-24.58 24.6 24.6-14.3 14.3-10.3-10.3-10.29 10.3zM63.31 0c34.93 0 63.3 28.36 63.3 63.3 0 34.93-28.37 63.3-63.3 63.3-34.94 0-63.31-28.37-63.31-63.3C0 28.36 28.37 0 63.31 0zm0 10.08c-29.36 0-53.23 23.87-53.23 53.23 0 29.36 23.87 53.22 53.23 53.22 29.35 0 53.22-23.86 53.22-53.22 0-29.36-23.87-53.23-53.22-53.23z"/>
                <path fill="currentColor" d="M42.14 75.52l21.17 21.18 21.18-21.18-10.05-10.06-11.13 11.13-11.13-11.13z"/>
              </svg>
              <div className="partner-name">Binance Smart Chain</div>
            </div>
            <div className="partner-card">
              <svg className="partner-logo-svg zama" viewBox="0 0 200 80" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="zamaGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#8b5cf6"/>
                    <stop offset="100%" stopColor="#a855f7"/>
                  </linearGradient>
                </defs>
                <text x="10" y="55" fontFamily="Arial, sans-serif" fontSize="48" fontWeight="900" fill="url(#zamaGradient)" letterSpacing="-2">ZAMA</text>
                <rect x="10" y="65" width="50" height="4" fill="url(#zamaGradient)" rx="2"/>
              </svg>
              <div className="partner-name">Zama FHE</div>
            </div>
            <div className="partner-card">
              <svg className="partner-logo-svg a16z" viewBox="0 0 200 60" xmlns="http://www.w3.org/2000/svg">
                <text x="10" y="45" fontFamily="'Courier New', monospace" fontSize="42" fontWeight="700" fill="currentColor" letterSpacing="-1">a16z</text>
              </svg>
              <div className="partner-name">Andreessen Horowitz</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <div className="features-grid">
          {t.features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-text">
            Built with <span className="highlight">Zama FHE</span> • Powered by <span className="highlight">Ethereum</span>
          </div>
          <div className="footer-links">
            <a href="https://github.com" target="_blank" rel="noopener noreferrer">GitHub</a>
            <a href="https://docs.example.com" target="_blank" rel="noopener noreferrer">Docs</a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">Twitter</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
